import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Shield, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PRIVACY_CONTENT = {
  fr: {
    title: "Politique de confidentialité",
    lastUpdated: "Dernière mise à jour : 24 février 2026",
    backToLogin: "Retour à la connexion",
    sections: [
      {
        title: "1. Introduction",
        content: `SpaceLeadX Inc. ("nous", "notre", "nos") s'engage à protéger la confidentialité de vos renseignements personnels. Cette Politique de confidentialité décrit comment nous collectons, utilisons, divulguons et protégeons vos informations lorsque vous utilisez notre service SpaceLeadX (le "Service").

Cette politique est conforme à la Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE) du Canada et aux lois provinciales applicables sur la protection de la vie privée.`
      },
      {
        title: "2. Renseignements que nous collectons",
        content: `Nous collectons les types de renseignements suivants :

**Informations de compte :**
• Nom complet
• Adresse courriel
• Mot de passe (chiffré)
• Informations de l'entreprise (nom, adresse, téléphone)

**Données d'utilisation :**
• Adresse IP
• Type de navigateur et appareil
• Pages visitées et actions effectuées
• Date et heure d'accès
• Données de référence (d'où vous venez)

**Données des prospects :**
• Informations soumises via vos formulaires
• Ces données sont collectées en votre nom et vous en êtes le responsable

**Informations de paiement :**
• Traitées par des processeurs tiers sécurisés
• Nous ne stockons pas les numéros de carte complets`
      },
      {
        title: "3. Comment nous utilisons vos informations",
        content: `Nous utilisons vos informations pour :

• Fournir et maintenir le Service
• Créer et gérer votre compte
• Traiter les transactions et envoyer des notifications
• Répondre à vos demandes de support
• Améliorer et personnaliser le Service
• Analyser l'utilisation et les tendances
• Détecter et prévenir la fraude
• Respecter nos obligations légales
• Vous envoyer des communications marketing (avec votre consentement)`
      },
      {
        title: "4. Base légale du traitement",
        content: `Nous traitons vos données personnelles sur les bases légales suivantes :

• **Exécution du contrat** : Pour fournir le Service auquel vous êtes inscrit
• **Consentement** : Pour les communications marketing et certains cookies
• **Intérêts légitimes** : Pour améliorer le Service et prévenir la fraude
• **Obligation légale** : Pour respecter les lois applicables`
      },
      {
        title: "5. Partage de vos informations",
        content: `Nous pouvons partager vos informations avec :

**Fournisseurs de services :**
• Hébergement cloud (serveurs sécurisés au Canada)
• Processeurs de paiement (Stripe)
• Services d'analyse
• Services de courriel

**Autres parties :**
• Avec votre consentement explicite
• Pour respecter une obligation légale
• Pour protéger nos droits ou ceux d'autrui
• En cas de fusion, acquisition ou vente d'actifs

Nous ne vendons jamais vos données personnelles à des tiers.`
      },
      {
        title: "6. Authentification Google OAuth",
        content: `Si vous choisissez de vous connecter via Google :

• Nous recevons votre nom, adresse courriel et photo de profil
• Nous n'accédons pas à votre compte Google ni à d'autres données
• Cette connexion est sécurisée par le protocole OAuth 2.0
• Vous pouvez révoquer l'accès depuis votre compte Google

Nous utilisons ces informations uniquement pour créer et authentifier votre compte SpaceLeadX.`
      },
      {
        title: "7. Cookies et technologies similaires",
        content: `Nous utilisons des cookies pour :

**Cookies essentiels :**
• Authentification et sécurité de session
• Préférences de langue

**Cookies d'analyse :**
• Comprendre l'utilisation du Service
• Améliorer l'expérience utilisateur

Vous pouvez contrôler les cookies via les paramètres de votre navigateur. La désactivation de certains cookies peut affecter le fonctionnement du Service.`
      },
      {
        title: "8. Sécurité des données",
        content: `Nous prenons la sécurité de vos données très au sérieux :

• Chiffrement SSL/TLS pour toutes les transmissions
• Chiffrement des mots de passe avec des algorithmes robustes
• Accès restreint aux données personnelles
• Surveillance et audit réguliers
• Serveurs hébergés au Canada dans des centres certifiés
• Sauvegardes régulières et plans de reprise

Aucun système n'est infaillible. Nous vous encourageons à utiliser des mots de passe forts et à protéger vos identifiants.`
      },
      {
        title: "9. Conservation des données",
        content: `Nous conservons vos données :

• **Données de compte** : Tant que votre compte est actif, puis 2 ans après la suppression
• **Données des prospects** : Selon vos paramètres de conservation (vous contrôlez)
• **Journaux d'accès** : 12 mois
• **Données de facturation** : 7 ans (obligation légale)

Vous pouvez demander la suppression de vos données à tout moment, sous réserve des obligations légales de conservation.`
      },
      {
        title: "10. Vos droits",
        content: `Conformément aux lois canadiennes sur la protection de la vie privée, vous avez le droit de :

• **Accès** : Obtenir une copie de vos données personnelles
• **Rectification** : Corriger des données inexactes
• **Suppression** : Demander la suppression de vos données
• **Portabilité** : Recevoir vos données dans un format structuré
• **Opposition** : Vous opposer à certains traitements
• **Retrait du consentement** : Retirer votre consentement à tout moment

Pour exercer ces droits, contactez-nous à privacy@spaceleadx.ai. Nous répondrons dans les 30 jours.`
      },
      {
        title: "11. Transferts internationaux",
        content: `Vos données sont principalement stockées et traitées au Canada. Si nous devons transférer des données vers d'autres pays, nous nous assurons que des protections adéquates sont en place conformément aux lois canadiennes.`
      },
      {
        title: "12. Données des mineurs",
        content: `Le Service n'est pas destiné aux personnes de moins de 18 ans. Nous ne collectons pas sciemment de données personnelles de mineurs. Si vous pensez qu'un mineur nous a fourni des données, contactez-nous pour que nous les supprimions.`
      },
      {
        title: "13. Modifications de cette politique",
        content: `Nous pouvons mettre à jour cette politique de temps en temps. Les modifications importantes seront notifiées par :

• Affichage sur le Service
• Notification par courriel

Nous vous encourageons à consulter cette politique régulièrement.`
      },
      {
        title: "14. Contact",
        content: `Pour toute question concernant cette Politique de confidentialité ou vos données personnelles :

**Responsable de la protection de la vie privée**
SpaceLeadX Inc.
Courriel : privacy@spaceleadx.ai
Adresse : Montréal, Québec, Canada

Pour déposer une plainte auprès d'une autorité de surveillance, vous pouvez contacter le Commissariat à la protection de la vie privée du Canada (CPVP).`
      }
    ]
  },
  en: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: February 24, 2026",
    backToLogin: "Back to login",
    sections: [
      {
        title: "1. Introduction",
        content: `SpaceLeadX Inc. ("we", "our", "us") is committed to protecting the privacy of your personal information. This Privacy Policy describes how we collect, use, disclose, and protect your information when you use our SpaceLeadX service (the "Service").

This policy complies with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy laws.`
      },
      {
        title: "2. Information We Collect",
        content: `We collect the following types of information:

**Account Information:**
• Full name
• Email address
• Password (encrypted)
• Company information (name, address, phone)

**Usage Data:**
• IP address
• Browser and device type
• Pages visited and actions taken
• Date and time of access
• Referral data (where you came from)

**Lead Data:**
• Information submitted through your forms
• This data is collected on your behalf and you are the data controller

**Payment Information:**
• Processed by secure third-party processors
• We do not store complete card numbers`
      },
      {
        title: "3. How We Use Your Information",
        content: `We use your information to:

• Provide and maintain the Service
• Create and manage your account
• Process transactions and send notifications
• Respond to your support requests
• Improve and personalize the Service
• Analyze usage and trends
• Detect and prevent fraud
• Comply with our legal obligations
• Send marketing communications (with your consent)`
      },
      {
        title: "4. Legal Basis for Processing",
        content: `We process your personal data on the following legal bases:

• **Contract Performance**: To provide the Service you subscribed to
• **Consent**: For marketing communications and certain cookies
• **Legitimate Interests**: To improve the Service and prevent fraud
• **Legal Obligation**: To comply with applicable laws`
      },
      {
        title: "5. Sharing Your Information",
        content: `We may share your information with:

**Service Providers:**
• Cloud hosting (secure servers in Canada)
• Payment processors (Stripe)
• Analytics services
• Email services

**Other Parties:**
• With your explicit consent
• To comply with legal obligations
• To protect our rights or those of others
• In case of merger, acquisition, or asset sale

We never sell your personal data to third parties.`
      },
      {
        title: "6. Google OAuth Authentication",
        content: `If you choose to sign in via Google:

• We receive your name, email address, and profile picture
• We do not access your Google account or other data
• This login is secured by the OAuth 2.0 protocol
• You can revoke access from your Google account

We use this information only to create and authenticate your SpaceLeadX account.`
      },
      {
        title: "7. Cookies and Similar Technologies",
        content: `We use cookies for:

**Essential Cookies:**
• Authentication and session security
• Language preferences

**Analytics Cookies:**
• Understanding Service usage
• Improving user experience

You can control cookies through your browser settings. Disabling certain cookies may affect Service functionality.`
      },
      {
        title: "8. Data Security",
        content: `We take your data security very seriously:

• SSL/TLS encryption for all transmissions
• Password encryption with robust algorithms
• Restricted access to personal data
• Regular monitoring and auditing
• Servers hosted in Canada in certified data centers
• Regular backups and recovery plans

No system is infallible. We encourage you to use strong passwords and protect your credentials.`
      },
      {
        title: "9. Data Retention",
        content: `We retain your data as follows:

• **Account Data**: As long as your account is active, then 2 years after deletion
• **Lead Data**: According to your retention settings (you control)
• **Access Logs**: 12 months
• **Billing Data**: 7 years (legal requirement)

You can request deletion of your data at any time, subject to legal retention requirements.`
      },
      {
        title: "10. Your Rights",
        content: `Under Canadian privacy laws, you have the right to:

• **Access**: Obtain a copy of your personal data
• **Rectification**: Correct inaccurate data
• **Deletion**: Request deletion of your data
• **Portability**: Receive your data in a structured format
• **Objection**: Object to certain processing
• **Withdraw Consent**: Withdraw your consent at any time

To exercise these rights, contact us at privacy@spaceleadx.ai. We will respond within 30 days.`
      },
      {
        title: "11. International Transfers",
        content: `Your data is primarily stored and processed in Canada. If we need to transfer data to other countries, we ensure adequate protections are in place in accordance with Canadian laws.`
      },
      {
        title: "12. Children's Data",
        content: `The Service is not intended for persons under 18 years of age. We do not knowingly collect personal data from minors. If you believe a minor has provided us with data, contact us so we can delete it.`
      },
      {
        title: "13. Changes to This Policy",
        content: `We may update this policy from time to time. Material changes will be notified by:

• Posting on the Service
• Email notification

We encourage you to review this policy regularly.`
      },
      {
        title: "14. Contact",
        content: `For any questions about this Privacy Policy or your personal data:

**Privacy Officer**
SpaceLeadX Inc.
Email: privacy@spaceleadx.ai
Address: Montreal, Quebec, Canada

To file a complaint with a supervisory authority, you may contact the Office of the Privacy Commissioner of Canada (OPC).`
      }
    ]
  }
};

export function PrivacyPage() {
  const location = useLocation();
  const lang = location.pathname.startsWith('/fr') ? 'fr' : 'en';
  const content = PRIVACY_CONTENT[lang] || PRIVACY_CONTENT.en;
  const otherLang = lang === 'fr' ? 'en' : 'fr';
  const otherPath = lang === 'fr' ? '/en/privacy-policy' : '/fr/politique-de-confidentialite';

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2">
            <span className="font-heading text-xl font-black tracking-tight">
              Space<span className="text-primary">LeadX</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link 
              to={otherPath}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {otherLang.toUpperCase()}
            </Link>
            <Button variant="outline" size="sm" asChild>
              <Link to="/login">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {content.backToLogin}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-card rounded-xl shadow-lg border border-border p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-black tracking-tight">
                {content.title}
              </h1>
              <p className="text-muted-foreground mt-1">
                {content.lastUpdated}
              </p>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-8">
            <Lock className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800 dark:text-green-200">
              {lang === 'fr' 
                ? 'Vos données sont protégées conformément aux lois canadiennes (LPRPDE)'
                : 'Your data is protected in accordance with Canadian laws (PIPEDA)'}
            </span>
          </div>

          {/* Sections */}
          <div className="prose prose-slate max-w-none">
            {content.sections.map((section, index) => (
              <section key={index} className="mb-8">
                <h2 className="text-xl font-bold text-foreground mb-4">
                  {section.title}
                </h2>
                <div className="text-muted-foreground whitespace-pre-line leading-relaxed">
                  {section.content}
                </div>
              </section>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center text-sm text-muted-foreground">
          <p>© 2026 SpaceLeadX Inc. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
          <div className="mt-2 flex justify-center gap-4">
            <Link 
              to={lang === 'fr' ? '/fr/conditions-dutilisation' : '/en/terms-of-use'}
              className="hover:text-primary transition-colors"
            >
              {lang === 'fr' ? "Conditions d'utilisation" : 'Terms of Use'}
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
